<template>
  <template v-if="!initializing">
    <div class="d-flex flex-column gap-20 text-primary">
      <div class="d-flex flex-column gap-20">
        <TarjetaProximaReservaAcciones
          v-for="reserva in reservasPagoPendiente"
          :key="reserva.id"
          from-mis-reservas
          :action="ACCIONES_TARJETA_RESERVA.PAY"
          :reserva="reserva"
          :grupo="grupo"
          :selectable="reservasPagoPendiente.length > 1"
          :selected="reservasSeleccionadas.includes(reserva.id)"
          @toggle="toggleSeleccionReserva(reserva.id)" />
      </div>

      <CardMetodoPago
        :pasarelas-pago="grupo.viaje.pasarelasPago"
        :initial-pasarela-pago="grupo.reservas[0].pasarelaPago"
        :importe-pagar="importePagar"
        @metodo-pago-seleccionado="handleMetodoPagoSeleccionado" />

      <CardReservaPagos
        v-if="reservasParaCalculoPago.length > 0"
        :selected-pasarela-pago="selectedPasarelaPago"
        is-pagos-sucesivos
        :precio-total-grupo="precioTotalGrupo"
        :reservas="reservasParaCalculoPago"
        @modo-pago="handleModoPagoChanged"
        @precio-calendarizado="handlePrecioCalendarizadoChanged"
        @total-pendiente="handleTotalPendienteChanged" />

      <p v-else-if="reservasPagoPendiente.length > 1" class="small text-primary mb-0">
        {{ $t('errors.seleccionaReservaContinuar') }}
      </p>

      <CardDatosFiscales
        v-if="shouldShowDatosFiscales"
        ref="datosFiscalesRef"
        @datos-fiscales-selected="handleDatosFiscalesSelected" />

      <div class="modal-footer border-0 my-2 text-center" style="justify-content: center">
        <MyGocuButton v-if="pagoExternoSelected" primary :label="labelPagar" @click="showInfoPagoExterno" />
        <MyGocuButton v-else primary :label="labelPagar" @click="realizarPagosPendientes()" />
      </div>
    </div>

    <div v-if="infoPagoRedsys">
      <form name="redsysPagoForm" :action="infoPagoRedsys.url" method="POST">
        <input type="hidden" name="Ds_SignatureVersion" :value="infoPagoRedsys.signatureVersion" />
        <input type="hidden" name="Ds_MerchantParameters" :value="infoPagoRedsys.params" />
        <input type="hidden" name="Ds_Signature" :value="infoPagoRedsys.signature" />
      </form>
    </div>
  </template>
</template>

<script setup>
  import { ref, computed, watch, nextTick, onBeforeMount } from 'vue';
  import { createToaster } from '@meforma/vue-toaster';
  import TarjetaProximaReservaAcciones from '../tarjetas/TarjetaProximaReservaAcciones.vue';
  import PasarelaPagoApiService from '@/services/api/PasarelaPagoApiService';
  import { alertAutoCloseWarningWithoutCallback, alertInfoHtml } from '@/services/utils/AlertUtils';
  import { controlErrorRealizarPago } from '@/services/utils/ErrorControl';
  import { useRouter } from 'vue-router';
  import CardReservaPagos from '@/components/card/Reserva/CardReservaPagos.vue';
  import CardDatosFiscales from '@/modules/viajesGrupos/components/CardDatosFiscales.vue';
  import { ESTADOS_RESERVA, ACCIONES_TARJETA_RESERVA } from '@/constants/reservaConstants';
  import { TIPOS_PAGO } from '@/constants/viajeConstants';
  import { useI18n } from 'vue-i18n';
  import { GruposViajesThankYou } from '@/router/RouterNames';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { toRawDeep } from '@/helpers/reactivityUtils';
  import { formatPrice } from '@/helpers/numberUtils';
  import { calculaPrecioTotalReservas } from '@/helpers/viajesGrupos';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { proximasExperiencias } from '@/store_pinia/proximasExperiencias';
  import CardMetodoPago from '@/components/card/Reserva/CardMetodoPago.vue';
  import { TIPOS_PASARELAS_PAGO } from '@/helpers/pagosUtils';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import { mergeReservaGrupoConStore } from '@/helpers/reservaPagoUtils';

  const props = defineProps({
    grupo: {
      type: Object,
      required: true,
    },
    reset: {
      type: Boolean,
    },
  });

  const emits = defineEmits(['closeModal']);

  const storeHabitacion = informacionHabitacion();
  const agenciaStore = agenciaStoreModule();
  const storeProxExp = proximasExperiencias();

  const { t } = useI18n();
  const loadingStore = useLoadingStore();
  const term = ref(false);
  const router = useRouter();
  const errorTerm = ref(false);
  const errorReserva = ref(null);
  const selectedDatosFiscalesId = ref(null);
  const modoPago = ref(TIPOS_PAGO.ALL);
  const precioCalendarizado = ref(0);
  const totalPendiente = ref(0);
  const infoPagoRedsys = ref(null);
  const datosFiscalesRef = ref(null);
  const selectedPasarelaPago = ref(null);
  const initializing = ref(true);
  const reservasSeleccionadas = ref([]);
  const reservasGrupo = ref([]);
  const toaster = createToaster({ position: 'top' });

  const reservasPagoPendiente = computed(() => {
    const reservas = reservasGrupo.value.length ? reservasGrupo.value : props.grupo.reservas ?? [];

    return reservas.filter(
      reserva => reserva.precioTotal - reserva.totalPagado > 0 && reserva.estado !== ESTADOS_RESERVA.CANCELADA
    );
  });

  const reservasParaCalculoPago = computed(() => {
    const reservasIds =
      reservasPagoPendiente.value.length === 1 ? [reservasPagoPendiente.value[0].id] : reservasSeleccionadas.value;
    const reservasGrupoList = reservasGrupo.value.length ? reservasGrupo.value : props.grupo.reservas ?? [];

    return reservasGrupoList.filter(reserva => reservasIds.includes(reserva.id)).map(getReservaParaCalculoPago);
  });

  const precioTotalGrupo = computed(() => calculaPrecioTotalReservas(reservasParaCalculoPago.value));

  const pagoExternoSelected = computed(
    () =>
      selectedPasarelaPago.value?.nombre === TIPOS_PASARELAS_PAGO.TRANSFERENCIA_MANUAL ||
      selectedPasarelaPago.value?.nombre === TIPOS_PASARELAS_PAGO.EFECTIVO
  );

  const importePagar = computed(() =>
    modoPago.value === TIPOS_PAGO.ALL ? totalPendiente.value : precioCalendarizado.value
  );

  const cantidadSelected = computed(() => `${formatPrice(importePagar.value)} ${agenciaStore.getCurrency}`);

  const labelPagar = computed(() => {
    return `${t('botones.pagar')} (${cantidadSelected.value})`;
  });

  const shouldShowDatosFiscales = computed(() => props.grupo.viaje.datosFiscalesEnabled);

  function getReservaParaCalculoPago(reservaGrupo) {
    return mergeReservaGrupoConStore(reservaGrupo, storeHabitacion.getResumenReservas);
  }

  function handleMetodoPagoSeleccionado(pasarelaPago) {
    selectedPasarelaPago.value = pasarelaPago;
  }

  function handleDatosFiscalesSelected(id) {
    selectedDatosFiscalesId.value = id;
  }

  function handleModoPagoChanged(modoPagoSeleccionado) {
    modoPago.value = modoPagoSeleccionado;
  }

  function handlePrecioCalendarizadoChanged(newPrecioCalendarizado) {
    precioCalendarizado.value = newPrecioCalendarizado;
  }

  function handleTotalPendienteChanged(newTotalPendiente) {
    totalPendiente.value = newTotalPendiente;
  }

  function toggleSeleccionReserva(reservaId) {
    const index = reservasSeleccionadas.value.indexOf(reservaId);
    if (index === -1) {
      reservasSeleccionadas.value.push(reservaId);
    } else {
      reservasSeleccionadas.value.splice(index, 1);
    }
  }

  function getReservasIdsParaPago() {
    return reservasPagoPendiente.value.length > 1
      ? reservasSeleccionadas.value
      : reservasPagoPendiente.value.map(reserva => reserva.id);
  }

  async function realizarPagosPendientes() {
    if (!selectedPasarelaPago.value?.id) {
      alertAutoCloseWarningWithoutCallback(3000, t('reserva.pago.error.metodoPagoSeleccionado'));
      return;
    }

    const reservasIds = getReservasIdsParaPago();
    if (reservasIds.length === 0) {
      toaster.error(t('errors.seleccionaReservaContinuar'));
      return;
    }

    try {
      loadingStore.show();
      errorTerm.value = false;
      errorReserva.value = null;

      if (shouldShowDatosFiscales.value) {
        if (selectedDatosFiscalesId.value === null) {
          selectedDatosFiscalesId.value = await datosFiscalesRef.value.submit();
        }
        if (!selectedDatosFiscalesId.value || selectedDatosFiscalesId.value === null) {
          return;
        }
      }

      const datosRedireccionDTO = await PasarelaPagoApiService.solicitaCobroSucesivo(
        reservasIds,
        false,
        null,
        modoPago.value,
        selectedDatosFiscalesId.value,
        selectedPasarelaPago.value.id
      );
      term.value = false;
      if (!datosRedireccionDTO.url) {
        router.push({
          name: GruposViajesThankYou,
          params: {
            grupoId: datosRedireccionDTO.grupoId,
          },
          query: {
            msg: datosRedireccionDTO.msgThankyou,
            integracionAlojamiento: datosRedireccionDTO.integracionAlojamiento,
          },
        });
      } else {
        if (datosRedireccionDTO.redsysInfo) {
          infoPagoRedsys.value = datosRedireccionDTO.redsysInfo;
          infoPagoRedsys.value.url = datosRedireccionDTO.url;
          await nextTick();
          document.redsysPagoForm.submit();
        } else {
          window.location.replace(datosRedireccionDTO.url);
        }
      }
    } catch (err) {
      if (err.response?.status === 409) {
        alertAutoCloseWarningWithoutCallback(4000, t('errors.pagoTransferencia'));
      } else {
        controlErrorRealizarPago(err);
      }
    } finally {
      loadingStore.hide();
    }
  }

  function showInfoPagoExterno() {
    const reservasIds = getReservasIdsParaPago();
    if (reservasIds.length === 0) {
      toaster.error(t('errors.seleccionaReservaContinuar'));
      return;
    }

    const infoPago =
      selectedPasarelaPago.value?.nombre === TIPOS_PASARELAS_PAGO.TRANSFERENCIA_MANUAL
        ? props.grupo.viaje?.infoPagoTransferenciaManual
        : props.grupo.viaje?.infoPagoEfectivo;
    alertInfoHtml(
      t('general.informacionPago') +
        ':<br><br>' +
        infoPago +
        '<br>' +
        t('subtitulo.totalPagar') +
        ' ' +
        cantidadSelected.value
    );
    emits('closeModal');
  }

  function addReservaToStore(reserva) {
    storeHabitacion.addReserva({
      ...reserva,
      usuario: {
        nombre: reserva.nombre,
        apellidos: reserva.apellidos,
        apellido: reserva.apellidos,
        fechaNacimiento: reserva.fechaNacimiento,
      },
    });
  }

  async function init() {
    try {
      loadingStore.show();
      await storeHabitacion.loadInformacionViaje(
        props.grupo.viaje.id,
        props.grupo.viaje.codigo ?? null,
        false,
        props.grupo.viaje.codigoPrevisualizacion ?? null
      );
      await storeProxExp.setGrupoSeleccionado(props.grupo.id, true, props.grupo.isB2b);
      const grupoRaw = toRawDeep(storeProxExp.getGrupoSeleccionado);
      storeHabitacion.clearReservas();
      storeHabitacion.setInformacionAlojamientoFromRGrupoDTO(grupoRaw);
      grupoRaw.reservas.forEach(reserva => addReservaToStore(reserva));
      reservasGrupo.value = grupoRaw.reservas;
      if (reservasPagoPendiente.value.length === 1) {
        reservasSeleccionadas.value = [reservasPagoPendiente.value[0].id];
      }
    } finally {
      initializing.value = false;
      loadingStore.hide();
    }
  }

  watch(
    () => props.reset,
    () => {
      term.value = false;
    }
  );

  onBeforeMount(async () => await init());
</script>
